import { getBlockDefinition } from '../helpers'
import { blockErrorTypes } from './constants'

const isTextMissing = text => !text?.trim()

export const isCustomTextEnabled = translations => Object.keys(translations).length > 0

export const buildEmptyTranslations = languages => Object.fromEntries(languages.map(language => [language, '']))

export const getMissingLanguages = (field, translations, languages) => {
  if (!field.required || !isCustomTextEnabled(translations)) return []

  return languages.filter(language => isTextMissing(translations[language]))
}

export const getTextError = (field, text) => {
  if (!field.required || !isTextMissing(text)) return null

  return blockErrorTypes.required
}

export const getTranslatedTextError = (field, translations, languages) => {
  if (getMissingLanguages(field, translations, languages).length === 0) return null

  return blockErrorTypes.required
}

const getDateLimitError = visibility => {
  if (!visibility.limitByDate || visibility.visible_from || visibility.visible_until) return null

  return blockErrorTypes.atLeastOne
}

const getCriteriaLimitError = visibility => {
  if (!visibility.limitByCriteria || visibility.visibility_criterion_ids.length > 0) return null

  return blockErrorTypes.required
}

export const pickErrors = entries => Object.fromEntries(entries.filter(([, error]) => error))

export const getBlockVisibilityErrors = visibility => {
  return pickErrors([
    ['limitByDate', getDateLimitError(visibility)],
    ['limitByCriteria', getCriteriaLimitError(visibility)],
  ])
}

export const getBlockName = (blockId, blockSettings, t) => {
  return blockSettings[blockId]?.internal_name || t(getBlockDefinition(blockId).label)
}

export const getFieldErrorCountKey = count => (count === 1 ? '{{count}} campo' : '{{count}} campos')

export const isBlockOffCanvas = ({ blockId, hiddenBlocks, showHiddenBlocks }) => {
  return !showHiddenBlocks && Boolean(hiddenBlocks[blockId])
}

export const countHiddenLayoutBlocks = (layout, hiddenBlocks) => {
  if (!layout) return 0

  return layout.filter(item => hiddenBlocks[item.i]).length
}

const isInactiveBlock = block => !block.isActive

const findBlock = (blocks, blockId) => blocks.find(block => block.blockId === blockId)

const getUniqueBlockIds = blocks => {
  const blockIds = blocks.map(block => block.blockId)

  return [...new Set(blockIds)]
}

const getHiddenReasons = ({ missingDataBlock, inactiveDateBlock, inactiveCriteriaBlock, notActiveOn }) => {
  return {
    missingData: missingDataBlock ? missingDataBlock.missingData : null,
    notActiveOn: inactiveDateBlock ? notActiveOn : null,
    criteriaNames: inactiveCriteriaBlock ? inactiveCriteriaBlock.criteriaNames : [],
  }
}

export const buildHiddenBlocks = ({ missingDataBlocks, dateLimitedBlocks, notActiveOn, criteriaLimitedBlocks }) => {
  const inactiveDateBlocks = dateLimitedBlocks.filter(isInactiveBlock)
  const inactiveCriteriaBlocks = criteriaLimitedBlocks.filter(isInactiveBlock)
  const hiddenBlockIds = getUniqueBlockIds([...missingDataBlocks, ...inactiveDateBlocks, ...inactiveCriteriaBlocks])

  const hiddenBlockEntries = hiddenBlockIds.map(blockId => {
    const missingDataBlock = findBlock(missingDataBlocks, blockId)
    const inactiveDateBlock = findBlock(inactiveDateBlocks, blockId)
    const inactiveCriteriaBlock = findBlock(inactiveCriteriaBlocks, blockId)
    const hiddenReasons = getHiddenReasons({ missingDataBlock, inactiveDateBlock, inactiveCriteriaBlock, notActiveOn })

    return [blockId, hiddenReasons]
  })

  return Object.fromEntries(hiddenBlockEntries)
}

const getEditorStorageKey = (storageKeyPrefix, storeId) => `${storageKeyPrefix}-${storeId}`

export const loadStoredEditorValue = (storageKeyPrefix, storeId) => {
  const storedValue = localStorage.getItem(getEditorStorageKey(storageKeyPrefix, storeId))

  return JSON.parse(storedValue)
}

export const saveStoredEditorValue = (storageKeyPrefix, storeId, value) => {
  localStorage.setItem(getEditorStorageKey(storageKeyPrefix, storeId), JSON.stringify(value))
}
